import React, { useCallback } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { CardHeader, Heading } from '@amzn/storm-ui';
import useIntl from '@i18n/useIntl';
import { Captcha, CaptchaResponse } from '@models/captcha';
import { handleKeyDown } from '@components/form/utils';
import AccountAttributesControllers from '@features/account-settings/components/AccountAttributesControllers';
import useSubmit from '@features/account-registration/hooks/useSubmit';
import RegistrationUserDetails from '@features/account-registration/components/RegistrationUserDetails/RegistrationUserDetails';
import BusinessDetails from '@features/account-registration/components/BusinessDetails/BusinessDetails';
import RegistrationFormFooter from '@features/account-registration/components/RegistrationFooter/RegistrationFormFooter';
import { ADSP_REGISTRATION_PAGE_ID } from 'src/pages/adspRegistration/constants';
import { REGISTRATION_FORM_TITLE_TRANSLATION_KEY } from '@features/account-registration/constants';
import {
  RegistrationCardContent,
  RegistrationCustomDivider,
  RegistrationFormCard,
  RegistrationFormWrapper,
} from '@features/account-registration/style';
import { AdProductGroup, BillToType } from 'src/shared/models/account';
import { useSearchParams } from 'react-router-dom';
import SellingAccounts from '@features/account-registration/components/SellingAccounts/SellingAccounts';
import AccountNameController from '@features/account-settings/components/AccountNameController';
import { ACCOUNT_DISPLAY_NAME_FIELD_EDIT_LABEL } from '@features/account-settings/constants';
import { getAccountCreationRequestFromFormData } from '@features/account-settings/utils';

interface AdspRegistrationFormProps {
  userName: string;
  switchAccountUrl: string;
  emailAddress: string;
  captcha?: Partial<Captcha>;
}

const AdspRegistrationForm = ({
  userName,
  emailAddress,
  switchAccountUrl,
  captcha = {},
}: AdspRegistrationFormProps) => {
  const { intl } = useIntl();
  const { onSubmit } = useSubmit();
  const [searchParams] = useSearchParams();
  const entityId = searchParams.get('entityId');

  const handleSubmit = useCallback(
    (data: FieldValues, captchaResponse?: CaptchaResponse) => {
      const submitPayload = getAccountCreationRequestFromFormData(data);
      onSubmit(submitPayload, captchaResponse);
    },
    [onSubmit],
  );

  const methods = useForm({
    mode: 'onBlur',
    defaultValues: {
      displayName: '',
      selectedSellingAccountId: null,
      sellingAccounts: [],
      industry: '',
      currency: '',
      timeZone: '',
      programType: AdProductGroup.ADSP,
      managerAccountId: entityId,
      billToType: BillToType.ManagerAccount,
      business: {
        selectedAddressId: null,
        businessName: '',
        phone: '',
        website: '',
        countryCode: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zipCode: '',
        businessRegistrationNumber: '',
      },
    },
  });

  return (
    <FormProvider {...methods}>
      <RegistrationFormWrapper onKeyDown={handleKeyDown}>
        <RegistrationFormCard>
          <CardHeader padding="medium">
            <RegistrationUserDetails
              userName={userName}
              emailAddress={emailAddress}
              switchAccountUrl={switchAccountUrl}
            />
          </CardHeader>
          <SellingAccounts
            pageId={ADSP_REGISTRATION_PAGE_ID}
            switchAccountUrl={switchAccountUrl}
          />
        </RegistrationFormCard>
        <RegistrationFormCard>
          <CardHeader padding="medium">
            <Heading renderAs="h2" size="xlarge">
              {intl.getMessage(REGISTRATION_FORM_TITLE_TRANSLATION_KEY)}
            </Heading>
          </CardHeader>
          <BusinessDetails />
          <RegistrationCustomDivider />
          <RegistrationCardContent>
            <AccountAttributesControllers managerAccountId={entityId} />
            <AccountNameController
              labelKey={ACCOUNT_DISPLAY_NAME_FIELD_EDIT_LABEL}
            />
          </RegistrationCardContent>
        </RegistrationFormCard>
      </RegistrationFormWrapper>
      <RegistrationFormFooter
        id={ADSP_REGISTRATION_PAGE_ID}
        captcha={captcha}
        onSubmit={handleSubmit}
        isTermsHidden={false}
        isDspRegistration={true}
      />
    </FormProvider>
  );
};

export default AdspRegistrationForm;
